// Node's built-in fetch ignores https_proxy: undici only reads it when
// NODE_USE_ENV_PROXY=1 is set before the runtime starts, which a library cannot
// do for its caller. So resolve the proxy ourselves and pass an explicit
// dispatcher — the agent works behind a proxy without the caller knowing.
import { Agent, ProxyAgent, type Dispatcher } from "undici";

let cached: Dispatcher | undefined;

export function proxyUrl(): string | undefined {
  for (const name of ["https_proxy", "HTTPS_PROXY", "http_proxy", "HTTP_PROXY"]) {
    const value = (process.env[name] ?? "").trim();
    // socks5 (all_proxy) is deliberately ignored: ProxyAgent speaks HTTP CONNECT.
    if (value && /^https?:\/\//i.test(value)) return value;
  }
  return undefined;
}

export function dispatcher(): Dispatcher {
  if (cached === undefined) {
    const url = proxyUrl();
    // Each caller owns its deadline through AbortSignal. Undici's independent
    // five-minute header/body timers must not truncate a longer model request.
    const timeouts = { headersTimeout: 0, bodyTimeout: 0 };
    cached = url ? new ProxyAgent({ uri: url, ...timeouts }) : new Agent(timeouts);
  }
  return cached;
}

type NativeFetchInit = NonNullable<Parameters<typeof globalThis.fetch>[1]>;
export const proxyFetch: typeof globalThis.fetch = (input, init) => {
  // Node and the installed undici package ship separate Dispatcher declarations.
  const proxy = dispatcher() as unknown as NativeFetchInit["dispatcher"];
  return globalThis.fetch(input, { ...init, ...(proxy ? { dispatcher: proxy } : {}) });
};

/** fetch with the proxy applied and a timeout, since a hung video call stalls an episode. */
export async function post(url: string, body: unknown, timeoutMs = 180_000, headers: Record<string, string> = {}): Promise<Response> {
  return proxyFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(timeoutMs),
  });
}
